import { Avatar, Badge, Group, Text } from '@mantine/core'
import { Grid } from '@mantine/core'
import { Select } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import avatars from '@/assets/avatars'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { getDefaultPskAvatar, getDefaultPskAvatarId } from '@/screens/SetupScreen/utils'
import type { ESKResponse } from '@/services/api/api.types'
import { isHelpModule } from '@/utils/contentModuleTables'
import { getRequiredFieldDescriptionProps } from '@/utils/forms'

const PSK_VALUE = 'PSK'

type ESKWithId = ESKResponse & { eskId: string }

export const TopicSettingsSection = observer(function TopicSettingsSection(props: CMFormSection) {
  const { authenticationStore } = useGlobalState()
  const { user } = authenticationStore
  const isSponsorAdmin = user?.isSponsorAdmin ?? false

  const fieldRefs = {
    selectEsk: useRef<HTMLInputElement>(null),
  }

  useEffect(() => {
    if (props.setFieldRefs) {
      props.setFieldRefs(fieldRefs as Record<string, React.RefObject<HTMLElement>>)
    }
  }, [fieldRefs, props.setFieldRefs])

  // Combine ESKs from sponsor + pool, filtering out duplicates and MSKs
  const allEsks = useMemo((): ESKWithId[] => {
    const sponsorEsks = props.sidekicksQuery.data?.eskTypes ?? []
    const poolEsks = props.sidekickPoolQuery.data?.eskTypes ?? []
    const mskNames = new Set((props.sidekicksQuery.data?.mskTypes ?? []).map(msk => msk.name))

    // Get types of sponsor's existing ESKs to filter pool duplicates
    const sponsorEskTypes = new Set(sponsorEsks.map(esk => esk.type))

    // Convert sponsor ESKs to ESKWithId format (use type as eskId)
    const sponsorEskOptions: ESKWithId[] = sponsorEsks
      .filter(esk => !mskNames.has(esk.name)) // Exclude any that share name with MSK
      .map(esk => ({
        ...esk,
        eskId: esk.type,
      }))

    // Add pool ESKs that aren't already in sponsor's list and aren't MSKs
    const poolEskOptions: ESKWithId[] = poolEsks
      .filter(esk => !sponsorEskTypes.has(esk.type) && !mskNames.has(esk.name))
      .map(esk => ({
        ...esk,
        eskId: esk.type,
      }))

    // Deduplicate by eskId to handle any duplicates within source arrays
    const combined = [...sponsorEskOptions, ...poolEskOptions]
    const uniqueByType = new Map<string, ESKWithId>()
    for (const esk of combined) {
      if (uniqueByType.has(esk.eskId)) {
        console.warn(`[ESK] Duplicate ESK detected in pool: ${esk.eskId} (${esk.name})`)
      } else {
        uniqueByType.set(esk.eskId, esk)
      }
    }
    return Array.from(uniqueByType.values())
  }, [props.sidekicksQuery.data, props.sidekickPoolQuery.data])

  // Build sidekick dropdown options with usage counts
  const currentTaskId = props.taskId

  // Store metadata separately (Mantine Select only accepts value/label/disabled/group)
  const sidekickMetadata = useMemo(() => {
    const contentModules = props.contentModulesQuery.data ?? []

    // Count how many CMs use each sidekick (excluding current CM and help modules)
    const usageCounts = new Map<string, number>()
    contentModules
      .filter(cm => cm.taskId !== currentTaskId && !isHelpModule(cm))
      .forEach(cm => {
        // Count PSK usage: selectEsk === 'PSK' or visibleToMsk === true
        if (cm.selectEsk === PSK_VALUE || cm.visibleToMsk) {
          const count = usageCounts.get(PSK_VALUE) ?? 0
          usageCounts.set(PSK_VALUE, count + 1)
        } else if (cm.selectEsk) {
          const count = usageCounts.get(cm.selectEsk) ?? 0
          usageCounts.set(cm.selectEsk, count + 1)
        }
      })

    // Build metadata map for avatar and usage count lookups
    const metadata = new Map<string, { avatarId: string; usageCount: number }>()

    // PSK metadata
    metadata.set(PSK_VALUE, {
      avatarId: getDefaultPskAvatarId(props.sidekicksQuery.data),
      usageCount: usageCounts.get(PSK_VALUE) ?? 0,
    })

    // ESK metadata
    allEsks.forEach(e => {
      metadata.set(e.eskId, {
        avatarId: e.avatarId,
        usageCount: usageCounts.get(e.eskId) ?? 0,
      })
    })

    return metadata
  }, [allEsks, props.contentModulesQuery.data, currentTaskId, props.sidekicksQuery.data])

  // Mantine Select data using proper grouped format
  const sidekickOptions = useMemo(() => {
    const groups: Array<{ group: string; items: Array<{ value: string; label: string }> }> = []

    // Personal Sidekick group
    groups.push({
      group: 'Personal',
      items: [{ value: PSK_VALUE, label: 'Personal Sidekick' }],
    })

    // Expert Sidekicks group (only if there are ESKs)
    if (allEsks.length > 0) {
      groups.push({
        group: 'Expert Sidekicks',
        items: allEsks.map(e => ({
          value: e.eskId,
          label: e.name,
        })),
      })
    }

    return groups
  }, [allEsks])

  // Get avatar for the selected sidekick
  const selectedSidekickAvatar = useMemo(() => {
    const selectedValue = props.moduleForm.values.selectEsk

    // Handle PSK selection
    if (selectedValue === PSK_VALUE) {
      return getDefaultPskAvatar(props.sidekicksQuery.data)
    }

    if (!selectedValue) return avatars.placeholder

    const esk = allEsks.find(e => e.eskId === selectedValue)
    return esk ? avatars[esk.avatarId] : avatars.placeholder
  }, [props.moduleForm.values.selectEsk, allEsks, props.sidekicksQuery.data])

  // Handle sidekick selection change - sync visibleToMsk for backend compatibility
  const handleSidekickChange = useCallback(
    (value: string | null) => {
      props.moduleForm.setFieldValue('selectEsk', value ?? '')
      // Sync visibleToMsk: true for PSK, false for ESKs
      props.moduleForm.setFieldValue('visibleToMsk', value === PSK_VALUE)
    },
    [props.moduleForm],
  )

  return (
    <Grid gutter="xxxl">
      {/* Sidekick Selector */}
      <Grid.Col span={{ base: 12, md: isSponsorAdmin ? 6 : 12 }}>
        <Select
          ref={fieldRefs.selectEsk}
          label="Sidekick"
          withAsterisk
          styles={{
            label: {
              fontSize: '15px',
            },
          }}
          scrollAreaProps={{
            type: 'always',
            scrollbarSize: 8,
          }}
          data={sidekickOptions}
          value={props.moduleForm.values.selectEsk}
          onChange={handleSidekickChange}
          error={props.moduleForm.errors.selectEsk}
          {...getRequiredFieldDescriptionProps(props.moduleForm, 'selectEsk', '')}
          leftSection={
            props.moduleForm.values.selectEsk ? <Avatar src={selectedSidekickAvatar} size="sm" radius="sm" /> : null
          }
          leftSectionWidth={props.moduleForm.values.selectEsk ? 40 : undefined}
          renderOption={({ option }) => {
            const metadata = sidekickMetadata.get(option.value)
            const usageCount = metadata?.usageCount ?? 0
            const avatarId = metadata?.avatarId
            return (
              <Group gap="sm" justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
                <Group gap="sm" wrap="nowrap">
                  <Avatar src={avatarId ? avatars[avatarId] : avatars.placeholder} size="sm" radius="sm" />
                  <Text size="sm" c={option.disabled ? 'dimmed' : undefined}>
                    {option.label}
                  </Text>
                </Group>
                {usageCount > 0 && (
                  <Badge size="sm" variant="light" color="blue">
                    {usageCount} module{usageCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </Group>
            )
          }}
        />
      </Grid.Col>
    </Grid>
  )
})
